import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["field"]
  static values = {
    formId: String
  }

  connect() {
    // Store original values to detect changes
    this.originalValues = new Map()
    this.fieldTargets.forEach(field => {
      this.originalValues.set(field.name, field.value)
    })
    // Which saved address the fields CURRENTLY
    // describe, not what the dropdown last showed.
    // A pick sets it, an edit clears it (CAL-413).
    this.savedAddressId = null
  }

  validateOnBlur(event) {
    const field = event.target
    const currentValue = field.value
    const originalValue = this.originalValues.get(field.name)

    // Only validate if the value has changed
    if (currentValue !== originalValue) {
      this.triggerValidation()
      // Update the original value for next comparison
      this.originalValues.set(field.name, currentValue)
    }
  }

  // Autocomplete picks (and saved-address picks)
  // populate the fields programmatically and dispatch
  // a window-scoped `address-selector:addressSelected`
  // event. We need to fire validation here too, or
  // the delivery route map's "Enviando a" panel
  // never updates after a pick (the user only
  // sees "Introduce arriba tu dirección de entrega"
  // until they blur a field).
  //
  // Sourcing the address payload from `event.detail
  // .address` (vs. reading from the live form inputs)
  // matters: this event ALSO kicks off async
  // state/city option loading on the state-selector
  // and city-selector controllers. At the moment this
  // handler fires, the state/city `<select>` values
  // are still blank (no matching option has been
  // appended yet), so reading them from the DOM would
  // make `hasMinimumFields` short-circuit and the map
  // would stay stale. The event payload carries the
  // complete address dictionary regardless of UI
  // hydration timing.
  validateOnAddressSelected(event) {
    const address = event?.detail?.address
    if (!address) return

    // Reset the original-value baseline so a later
    // blur on these same fields doesn't fire another
    // identical validation.
    this.fieldTargets.forEach(field => {
      this.originalValues.set(field.name, field.value)
    })
    this.triggerValidationFromAddress(
      address,
      event?.detail?.location,
      event?.detail?.savedAddressId
    )
  }

  triggerValidation() {
    const form = this.externalForm()
    if (!form) return

    this.updateExternalFormFields(form)
    this.discardPickedAddress(form)

    // Only submit if minimum required address
    // fields are filled: geocoding fails with
    // incomplete data and shows false warnings.
    if (!this.hasMinimumFields(form)) return

    this.copySelectedAddressId(form)
    form.requestSubmit()
  }

  // A hand-edit makes the text a DIFFERENT address
  // from the one that was picked, so everything the
  // pick brought with it stops applying (CAL-413):
  //
  // - The pin. Left alone, the server sees supplied
  //   coordinates and skips the geocode, the quote
  //   prices that point, and the signed token agrees
  //   with the pin the order posts: every guard
  //   consistent about the OLD place while the text
  //   says the new one. The courier drives to the
  //   old one.
  // - The saved-address id. Left alone, the server
  //   reads a new address as a saved one and skips
  //   asking the customer to confirm its pin.
  //
  // We own the validation form, so we blank the
  // coordinates on it here. The order form's pin
  // fields and the "adjusted" badge belong to
  // delivery-pin, so it answers this event and drops
  // its override and its remembered address pin.
  //
  // Unconditional, and before the minimum-fields
  // gate: an edit that leaves the address incomplete
  // still invalidates the pin, and the customer can
  // still submit the ORDER with whatever is sitting
  // in those hidden fields.
  discardPickedAddress(form) {
    this.savedAddressId = null
    this.setPinFields(form, null)
    this.dispatch("addressEdited")
  }

  triggerValidationFromAddress(
    address, location, savedAddressId
  ) {
    const form = this.externalForm()
    if (!form) return

    // Populate hidden fields from the event payload
    // (not the live form inputs; see note on
    // `validateOnAddressSelected`).
    const required = [
      "street_address", "suburb", "city",
      "state", "country", "postal_code"
    ]
    required.forEach(field => {
      this.setExternalFormField(
        form, field, address[field] ?? ""
      )
    })
    this.setPinFields(form, location)
    // Only a saved-address pick carries one. An
    // autocomplete pick is a brand new address, and
    // says so by leaving this undefined, even when
    // the dropdown is still showing whichever saved
    // address the customer started from.
    this.savedAddressId = savedAddressId ?? null

    if (!this.hasMinimumFields(form)) return

    this.copySelectedAddressId(form)
    form.requestSubmit()
  }

  // A picked place brings the pin Google already
  // resolved for it, and a saved address brings the
  // one it was created with (CAL-413). Sending it lets
  // the server validate THAT point instead of
  // geocoding the text we assembled out of it.
  //
  // This controller owns the submit for a pick, so it
  // owns what rides on it. The delivery-pin controller
  // answers the same event, but only to write the
  // order's own hidden pin fields: if it submitted
  // too, the second request would abort the first,
  // which by then has already reached Rails and
  // geocoded.
  //
  // Blanking is as load-bearing as writing: a pick
  // with no pin behind a pick that had one would
  // otherwise validate the previous address's point
  // against the new address. The server reads these
  // with `.present?`, so "" and absent are the same
  // thing to it.
  setPinFields(form, location) {
    const { latitude, longitude } = location || {}
    const present =
      latitude != null && longitude != null

    this.setExternalFormField(
      form, "latitude", present ? latitude : ""
    )
    this.setExternalFormField(
      form, "longitude", present ? longitude : ""
    )
  }

  externalForm() {
    const form = document.getElementById(
      this.formIdValue
    )
    if (!form) {
      console.error(
        `Form with id "${this.formIdValue}" not found`
      )
      return null
    }
    return form
  }

  // The id tells the server whether this is a new
  // address, which is what decides if the customer is
  // asked to confirm its pin. So it has to describe
  // the text in the fields RIGHT NOW.
  //
  // This used to read the saved-address `<select>`,
  // which was wrong twice over (CAL-413):
  //
  // - The picker is a SIBLING of this controller's
  //   element, not a descendant: `render_address_
  //   selector` and the AddressFormComponent that
  //   carries `address-field-validator` are two
  //   children of the same wrapper in
  //   delivery_restaurant_orders/new_view.rb. So
  //   `this.element.querySelector` found nothing on
  //   the delivery page and the id was never posted
  //   at all, which made every components validation
  //   look like a new address.
  // - Even where it did resolve, the dropdown keeps
  //   showing whichever saved address the customer
  //   arrived on. Typing a new address, or picking
  //   one from autocomplete, never touches it.
  //
  // Blanking an existing field is the load-bearing
  // half: a previous pick leaves its id sitting on
  // this form, so skipping the write would keep
  // posting it.
  copySelectedAddressId(form) {
    const id = this.savedAddressId ?? ""

    let hiddenField = form.querySelector(
      'input[name="selected_address_id"]'
    )
    if (!hiddenField) {
      // Nothing to say and nothing to correct: a
      // form with no saved-address picker behind it
      // (guest checkout, shipping) never had this
      // field and doesn't grow one now.
      if (!id) return

      hiddenField =
        document.createElement('input')
      hiddenField.type = 'hidden'
      hiddenField.name = 'selected_address_id'
      form.appendChild(hiddenField)
    }
    hiddenField.value = id
  }

  setExternalFormField(form, fieldName, value) {
    const inputName =
      `address_components[${fieldName}]`
    let hiddenField = form.querySelector(
      `input[name="${inputName}"]`
    )
    if (!hiddenField) {
      hiddenField =
        document.createElement('input')
      hiddenField.type = 'hidden'
      hiddenField.name = inputName
      form.appendChild(hiddenField)
    }
    hiddenField.value = value
  }

  hasMinimumFields(form) {
    const required = [
      "street_address", "city", "state", "country"
    ]
    return required.every(field => {
      const input = form.querySelector(
        `input[name="address_components[${field}]"]`
      )
      return input && input.value.trim() !== ""
    })
  }

  updateExternalFormFields(form) {
    // Get only the validatable field values
    // (the ones marked as targets). Support
    // both nested and flat field naming.
    this.fieldTargets.forEach(field => {
      // Try nested: [address_attributes][x]
      let match = field.name.match(
        /\[address_attributes\]\[([^\]]+)\]/
      )
      if (match) {
        this.setExternalFormField(
          form, match[1], field.value
        )
        return
      }
      // Try flat: [delivery_x] or [shipping_x]
      match = field.name.match(
        /\[(?:delivery|shipping)_([^\]]+)\]/
      )
      if (match) {
        this.setExternalFormField(
          form, match[1], field.value
        )
      }
    })
  }
}